import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td,
    HStack, VStack, useToast, Badge, Spinner, Progress,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
    ModalFooter, ModalCloseButton, FormControl, FormLabel,
    Select, Checkbox, useDisclosure, IconButton, Tooltip,
    Code, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText,
    Divider, Alert, AlertIcon, AlertDescription,
} from "@chakra-ui/react";
import { RepeatIcon, AddIcon, InfoOutlineIcon } from "@chakra-ui/icons";
import { Activity } from "lucide-react";
import { listarMigracoes, getMigracao, iniciarMigracao } from "../services/api";
import ScrollToTop from "../components/ScrollToTop";

const TIPO_LABELS = {
    "migrar-legado": "Migrar Legado",
    "importar-externos": "Importar Externos",
};

const FORNECEDORES = {
    "migrar-legado": [
        { value: "all", label: "Todos (morelate + van + testebr)" },
        { value: "morelate", label: "MORELATE" },
        { value: "van", label: "VANNUCCI" },
        { value: "testebr", label: "BRCOMPANY" },
    ],
    "importar-externos": [
        { value: "all", label: "Todos (ISAPA + LNG)" },
        { value: "isapa", label: "ISAPA" },
        { value: "lng", label: "LNG" },
    ],
};

const DESCRICOES = {
    "migrar-legado": "Varre fotos/ no servidor SFTP, faz match por código de fornecedor e registra como aprovadas. Usa SSH para calcular hashes em batch. ~15–30 min para 80k arquivos.",
    "importar-externos": "Baixa imagens de ISAPA e LNG via HTTP, salva no servidor e registra como pendentes para moderação. ~1h para todos os produtos.",
};

const statusColor = { rodando: "blue", concluido: "green", falhou: "red" };
const statusLabel = { rodando: "Rodando", concluido: "Concluído", falhou: "Falhou" };

const StatBox = ({ label, value, color = "gray.800" }) => (
    <Box textAlign="center" minW="60px">
        <Text fontSize="xs" color="gray.500" mb={0.5}>{label}</Text>
        <Text fontSize="md" fontWeight="bold" color={color}>{value ?? 0}</Text>
    </Box>
);

const JobRow = ({ job, onRefresh }) => {
    const isRunning = job.status === "rodando";
    const perc = job.perc_progresso ?? 0;

    return (
        <Box bg="white" borderRadius="md" boxShadow="sm" p={4} mb={3}>
            <Flex justify="space-between" align="flex-start" wrap="wrap" gap={2}>
                <Box>
                    <HStack spacing={2} mb={1}>
                        <Badge colorScheme={statusColor[job.status]} fontSize="xs">
                            {statusLabel[job.status]}
                            {isRunning && <Spinner size="xs" ml={1} />}
                        </Badge>
                        <Text fontWeight="semibold" fontSize="sm">
                            {TIPO_LABELS[job.tipo] || job.tipo}
                        </Text>
                        <Badge variant="outline" colorScheme="purple" fontSize="xs">
                            {job.fornecedor}
                        </Badge>
                        {job.duracao && (
                            <Text fontSize="xs" color="gray.500">⏱ {job.duracao}</Text>
                        )}
                    </HStack>
                    {job.etapa && (
                        <Text fontSize="xs" color="gray.500" mb={1}>
                            {job.etapa}
                        </Text>
                    )}
                </Box>
                <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">
                    #{job.id} · {job.iniciado_at
                        ? new Date(job.iniciado_at).toLocaleString("pt-BR")
                        : "—"}
                </Text>
            </Flex>

            {(isRunning || job.total > 0) && (
                <Box mb={3}>
                    <Flex justify="space-between" mb={1}>
                        <Text fontSize="xs" color="gray.500">
                            {job.processados?.toLocaleString("pt-BR")} / {job.total?.toLocaleString("pt-BR")} arquivos
                        </Text>
                        <Text fontSize="xs" fontWeight="semibold" color={isRunning ? "blue.600" : "green.600"}>
                            {perc}%
                        </Text>
                    </Flex>
                    <Progress
                        value={perc}
                        size="sm"
                        colorScheme={isRunning ? "blue" : "green"}
                        borderRadius="full"
                        hasStripe={isRunning}
                        isAnimated={isRunning}
                    />
                </Box>
            )}

            <SimpleGrid columns={{ base: 3, sm: 6 }} spacing={2} pt={1} borderTop="1px solid" borderColor="gray.100">
                <StatBox label="Inseridas" value={job.inseridas} color="green.600" />
                <StatBox label="Matched" value={job.matched} color="blue.600" />
                <StatBox label="Duplicadas" value={job.duplicadas} />
                <StatBox label="Órfãs" value={job.orfas} color="orange.500" />
                <StatBox label="Bloqueadas" value={job.bloqueadas} color="red.500" />
                <StatBox label="Erros" value={job.erros} color="red.600" />
            </SimpleGrid>

            {job.mensagem_erro && (
                <Alert status="error" mt={3} borderRadius="md" py={2}>
                    <AlertIcon boxSize={4} />
                    <AlertDescription fontSize="xs" fontFamily="mono" noOfLines={3}>
                        {job.mensagem_erro}
                    </AlertDescription>
                </Alert>
            )}
        </Box>
    );
};

const MigracaoImagens = () => {
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [iniciando, setIniciando] = useState(false);
    const [form, setForm] = useState({ tipo: "migrar-legado", fornecedor: "all", dry_run: false });
    const intervalRef = useRef(null);

    const fetchJobs = useCallback(async (silencioso = false) => {
        if (!silencioso) setLoading(true);
        try {
            const res = await listarMigracoes();
            setJobs(res.data);
        } catch {
            if (!silencioso) toast({ title: "Erro ao carregar migrações", status: "error", duration: 3000 });
        }
        if (!silencioso) setLoading(false);
    }, [toast]);

    useEffect(() => { fetchJobs(); }, [fetchJobs]);

    useEffect(() => {
        const temRodando = jobs.some(j => j.status === "rodando");
        if (temRodando) {
            if (!intervalRef.current) {
                intervalRef.current = setInterval(() => fetchJobs(true), 3000);
            }
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [jobs, fetchJobs]);

    const handleIniciar = async () => {
        setIniciando(true);
        try {
            await iniciarMigracao(form);
            toast({ title: "Migração iniciada!", description: "Acompanhe o progresso abaixo.", status: "success", duration: 3000 });
            onClose();
            fetchJobs();
        } catch (e) {
            toast({
                title: "Erro ao iniciar migração",
                description: e.response?.data?.error || "Verifique se o worker de fila está rodando.",
                status: "error", duration: 5000,
            });
        }
        setIniciando(false);
    };

    const temRodando = jobs.some(j => j.status === "rodando");

    return (
        <Box ml={{ base: 0, md: "64px" }} pt="56px" minH="100vh" bg="gray.50">
            <ScrollToTop />
            <Box maxW="1100px" mx="auto" py={{ base: 4, md: 6 }} px={{ base: 3, md: 8 }}>
                <Flex justify="space-between" align={{ base: "flex-start", md: "center" }}
                    mb={5} direction={{ base: "column", md: "row" }} gap={2}>
                    <Box>
                        <HStack mb={1}>
                            <Activity size={20} color="#F68633" />
                            <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">Migração de Imagens</Text>
                            {temRodando && <Spinner size="sm" color="blue.500" />}
                        </HStack>
                        <Text fontSize="sm" color="gray.600">
                            Monitore e inicie processos de importação de imagens legadas ou de fornecedores externos.
                            {temRodando && <Text as="span" color="blue.600" fontWeight="semibold"> · Atualizando automaticamente...</Text>}
                        </Text>
                    </Box>
                    <HStack>
                        <Tooltip label="Atualizar lista" hasArrow>
                            <IconButton icon={<RepeatIcon />} onClick={() => fetchJobs()} size="sm" variant="outline" aria-label="Atualizar" />
                        </Tooltip>
                        <Button leftIcon={<AddIcon />} colorScheme="orange" size="sm" onClick={onOpen}>
                            Nova Migração
                        </Button>
                    </HStack>
                </Flex>

                <Alert status="info" borderRadius="md" mb={5} fontSize="sm">
                    <AlertIcon />
                    <AlertDescription>
                        Para usar o botão "Nova Migração", o worker de fila precisa estar ativo no servidor:
                        <Code ml={2} fontSize="xs">php artisan queue:work --timeout=7200</Code>
                        <br />
                        Alternativamente, rode o comando diretamente via SSH — o progresso aparecerá aqui automaticamente.
                    </AlertDescription>
                </Alert>

                {loading ? (
                    <Flex justify="center" align="center" h="200px">
                        <Spinner size="xl" color="orange.500" />
                    </Flex>
                ) : jobs.length === 0 ? (
                    <Flex justify="center" align="center" h="200px" direction="column" gap={3}>
                        <Activity size={40} color="#CBD5E0" />
                        <Text color="gray.400">Nenhuma migração registrada ainda.</Text>
                    </Flex>
                ) : (
                    jobs.map(job => <JobRow key={job.id} job={job} onRefresh={fetchJobs} />)
                )}
            </Box>

            <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
                <ModalOverlay />
                <ModalContent mx={3}>
                    <ModalHeader fontSize="md">Nova Migração</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <FormControl>
                                <FormLabel fontSize="sm">Comando</FormLabel>
                                <Select size="sm" value={form.tipo}
                                    onChange={e => setForm(f => ({ ...f, tipo: e.target.value, fornecedor: "all" }))}>
                                    <option value="migrar-legado">Migrar Legado (fotos/ do servidor)</option>
                                    <option value="importar-externos">Importar Externos (ISAPA / LNG)</option>
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="sm">Fornecedor</FormLabel>
                                <Select size="sm" value={form.fornecedor}
                                    onChange={e => setForm(f => ({ ...f, fornecedor: e.target.value }))}>
                                    {(FORNECEDORES[form.tipo] || []).map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box bg="blue.50" borderRadius="md" p={3}>
                                <Text fontSize="xs" color="blue.700">{DESCRICOES[form.tipo]}</Text>
                            </Box>

                            <FormControl>
                                <Checkbox size="sm" isChecked={form.dry_run}
                                    onChange={e => setForm(f => ({ ...f, dry_run: e.target.checked }))}>
                                    <Text fontSize="sm">Dry-run (não altera o banco)</Text>
                                </Checkbox>
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button size="sm" variant="ghost" mr={3} onClick={onClose}>Cancelar</Button>
                        <Button size="sm" colorScheme="orange" isLoading={iniciando} onClick={handleIniciar}>
                            Iniciar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default MigracaoImagens;
